import React, { useState, useEffect } from 'react';
import { 
  CheckCircle2Icon, 
  AlertCircleIcon, 
  InfoIcon, 
  XCircleIcon 
} from 'lucide-react';

const toastTypes = {
  success: {
    icon: CheckCircle2Icon,
    bgColor: 'bg-custom-yellow',
    textColor: 'text-white-800',
    borderColor: 'border-black-200'
  },
  error: {
    icon: XCircleIcon,
    bgColor: 'bg-red-50',
    textColor: 'text-red-800',
    borderColor: 'border-red-200'
  },
  warning: {
    icon: AlertCircleIcon,
    bgColor: 'bg-yellow-50',
    textColor: 'text-yellow-800',
    borderColor: 'border-yellow-200'
  },
  info: {
    icon: InfoIcon,
    bgColor: 'bg-blue-50',
    textColor: 'text-blue-800',
    borderColor: 'border-blue-200'
  }
};

const Toast = ({ 
  message, 
  type = 'info', 
  duration = 3000, 
  onClose 
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const { 
    icon: Icon, 
    bgColor, 
    textColor, 
    borderColor 
  } = toastTypes[type] || toastTypes.info;

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      if (onClose) onClose();
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  if (!isVisible) return null;

  return (
    <div 
      className={`
        fixed top-24 right-4 z-50 
        ${bgColor} ${textColor} ${borderColor}
        border rounded-lg shadow-lg 
        flex items-center p-4 
        transition-all duration-300 ease-in-out
        animate-slide-in
      `}
    >
      <Icon className="mr-3 h-6 w-6" />
      <div className="flex-grow">{message}</div>
      <button 
        onClick={() => {
          setIsVisible(false);
          if (onClose) onClose();
        }}
        className="ml-4 hover:bg-gray-100 rounded-full p-1"
      >
        <XCircleIcon className="h-5 w-5 opacity-50 hover:opacity-100" />
      </button>
    </div>
  );
};

// Custom hook for managing toasts
export const useToast = () => {
  const [toasts, setToasts] = useState([]);

  const addToast = (message, type = 'info', duration = 3000) => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type, duration }]);
    return id;
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  const ToastContainer = () => (
    <div className="fixed text-white top-4 right-4 z-50 space-y-2">
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          onClose={() => removeToast(toast.id)}
        />
      ))}
    </div>
  );

  return { addToast, ToastContainer };
};

export default Toast;